import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { renderSkeletonTable } from "../context/Customfunction";
import { Button } from "react-bootstrap";
import { FiSearch, FiChevronDown, FiChevronRight } from "react-icons/fi";
import { useVisionContext } from "../Components/ContextAPI/VisionContext";

const CustomTable = ({
  columns,
  data = [],
  loading = false,
  onAddClick,
  addButtonLabel = "Add",
  showAddButton = true,
  showActions = false,
  onEdit = () => {},
  onDelete = () => {},
  pagination,
  onPaginationChange,
  rowCount = 0,
  searchTerm = null,
  onSearchChange = () => {},
  enableRowExpansion = false,
  permissions = [],
  alerts = [],
  handleToggleChange = () => {},
}) => {
  const [expandedModules, setExpandedModules] = useState({});
  const { showCheckInCheckOut } = useVisionContext();

  const table = useMaterialReactTable({
    columns: useMemo(() => {
      if (!showActions) return columns;
      return [
        ...columns,
        {
          id: "actions",
          header: "Actions",
          size: 120,
          enableColumnOrdering: false,
          enableSorting: false,
          Cell: ({ row }) => (
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => onEdit(row.original)}
              >
                Edit
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => onDelete(row.original)}
              >
                Delete
              </Button>
            </div>
          ),
        },
      ];
    }, [columns, showActions, onEdit, onDelete]),

    data,
    enableRowSelection: false,
    enableColumnOrdering: true,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    positionActionsColumn: "last",

    muiTableHeadCellProps: {
      sx: { backgroundColor: "#F5F6F8" },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: "12px", border: "1px solid #e0e0e0" },
    },

    enableExpanding: enableRowExpansion,
    renderDetailPanel: enableRowExpansion
      ? ({ row }) => {
          const groupedPermissions = permissions.reduce((acc, perm) => {
            if (!acc[perm.module]) acc[perm.module] = [];
            acc[perm.module].push(perm);
            return acc;
          }, {});

          return (
            <div
              style={{
                background: "#f9f9f9",
                padding: "16px",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              {/* Permissions Section */}
              <div>
                <h6 style={{ marginBottom: "12px", fontWeight: "600" }}>
                  Permissions
                </h6>

                {Object.keys(groupedPermissions).map((module) => (
                  <div
                    key={module}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      marginBottom: "12px",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {/* Module Header */}
                    <div
                      onClick={() =>
                        setExpandedModules((prev) => ({
                          ...prev,
                          [module]: !prev[module],
                        }))
                      }
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        background: "#f1f1f1",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      <span>{module.replace("_", " ").toUpperCase()}</span>
                      {expandedModules[module] ? (
                        <FiChevronDown />
                      ) : (
                        <FiChevronRight />
                      )}
                    </div>

                    {/* Permissions Grid */}
                    {expandedModules[module] && (
                      <div
                        style={{
                          padding: "12px 16px",
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(220px, 1fr))",
                          gap: "12px",
                        }}
                      >
                        {groupedPermissions[module].map((perm) => (
                          <div
                            key={perm._id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 12px",
                              border: "1px solid #eee",
                              borderRadius: "6px",
                              background: "#fafafa",
                            }}
                          >
                            <span style={{ fontSize: "13px" }}>
                              {perm.name}
                            </span>
                            <label className="switch">
                              <input
                                type="checkbox"
                                defaultChecked={row.original.permissions?.includes(
                                  perm._id
                                )}
                                onChange={(e) =>
                                  handleToggleChange({
                                    userId: row.original._id,
                                    type: "permission",
                                    valueId: perm._id,
                                    checked: e.target.checked,
                                  })
                                }
                              />
                              <span className="slider round"></span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Alerts Section */}
              <div>
                <h6 style={{ marginBottom: "12px", fontWeight: "600" }}>
                  Alerts
                </h6>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {alerts?.map((alert) => (
                    <div
                      key={alert._id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        border: "1px solid #eee",
                        borderRadius: "6px",
                        background: "#fafafa",
                      }}
                    >
                      <span style={{ fontSize: "13px" }}>
                        {alert.action_name}
                      </span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          defaultChecked={row.original.action_types?.includes(
                            alert._id
                          )}
                          onChange={(e) =>
                            handleToggleChange({
                              userId: row.original._id,
                              type: "alert",
                              valueId: alert._id,
                              checked: e.target.checked,
                            })
                          }
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }
      : undefined,
  });

  return (
    <div className="p-4">
      {showAddButton && (
        <div className="d-flex align-items-center gap-4 mb-4">
          <div className="searchBox flex-grow-1">
            <button className="searchIcon">
              <FiSearch size={22} />
            </button>
            <input
              type="text"
              placeholder="Search by name"
              className="form-control"
              value={searchTerm}
              onChange={onSearchChange}
            />
          </div>
          <button onClick={onAddClick} className="mainBlue2 py-2 px-4 ms-3">
            {addButtonLabel}
          </button>
        </div>
      )}
      {loading ? (
        renderSkeletonTable(columns)
      ) : (
        <MaterialReactTable table={table} />
      )}
    </div>
  );
};

export default CustomTable;
